import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mail, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductInquiryDialog = ({ open, product, onClose }) => {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && product) {
      setForm((f) => ({
        ...f,
        message: f.message || `Hi — I'm interested in the ${product.name}${product.model ? ` (${product.model})` : ''}. Please let me know availability and next steps.`,
      }));
    }
    if (!open) {
      setForm({ customer_name: '', customer_email: '', customer_phone: '', message: '' });
    }
  }, [open, product]);

  if (!product) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.customer_name.trim()) { toast.error('Please enter your name'); return; }
    if (!form.customer_email.trim()) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/catalog/inquiry`, {
        product_id: product.id,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        message: form.message.trim(),
      });
      toast.success('Inquiry sent — we\'ll be in touch shortly.');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send inquiry. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="shop-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-red-400" /> Ask about this product
          </DialogTitle>
          <div className="text-xs text-gray-500 mt-1">
            About: <span className="text-gray-300">{product.name}</span>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-widest">Name *</Label>
            <Input data-testid="shop-inquiry-name" value={form.customer_name} onChange={set('customer_name')} className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest">Email *</Label>
              <Input data-testid="shop-inquiry-email" type="email" value={form.customer_email} onChange={set('customer_email')} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest">Phone</Label>
              <Input data-testid="shop-inquiry-phone" value={form.customer_phone} onChange={set('customer_phone')} className="bg-slate-800 border-slate-700 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-widest">Message</Label>
            <Textarea data-testid="shop-inquiry-message" value={form.message} onChange={set('message')} rows={4} className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 text-gray-300 hover:bg-slate-800">Cancel</Button>
            <Button type="submit" data-testid="shop-inquiry-submit" disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white">
              <Send className="w-4 h-4 mr-2" /> {submitting ? 'Sending…' : 'Send inquiry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductInquiryDialog;
